import { roundTo2Decimals } from '@principle-theorem/accounting';
import {
  ChartedItemType,
  ITreatmentPackagePriceOverride,
  ServiceCodeGroupType,
  TreatmentFeeMode,
  type IChartedSurface,
  type IChartedTreatment,
  type IFeeSchedule,
  type IInvoiceReference,
  type IPricedServiceCodeEntry,
  type IPricingRule,
  type IScopeRef,
  type IStaffer,
  type ITreatmentConfiguration,
  type ITreatmentPackage,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  Firestore,
  isSameRef,
  reduceToSingleArrayFn,
  toNamedDocument,
  type AtLeast,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, flatten, uniqBy } from 'lodash';
import { v4 as uuid } from 'uuid';
import { ChartedItem } from '../core/charted-item';
import { FeeSchedule } from '../fees/fee-schedule/fee-schedule';
import { type FeeScheduleManager } from '../fees/fee-schedule/fee-schedule-manager';
import { PricedServiceCodeGroup } from '../service-codes/ada-code-category';
import { ChartedServiceExclusiveGroup } from '../service-codes/charted-service-exclusive-group';
import { ChartedServiceSmartGroup } from '../service-codes/charted-service-smart-group';
import {
  determinePricingRule,
  PricedServiceCodeEntry,
} from '../service-codes/service-code';
import { ChartedItemTotalCalculator } from './charted-item-total-calculator';
import { ScopedGroupTranslator } from './scoped-group-translator';
import { TreatmentConfiguration } from './treatment-configuration';
import { TreatmentPackage } from './treatment-package';

export class ChartedTreatment extends ChartedItem {
  static override init(
    overrides: AtLeast<IChartedTreatment, 'config' | 'feeSchedule' | 'scopeRef'>
  ): IChartedTreatment {
    return {
      uuid: uuid(),
      type: ChartedItemType.ChartedTreatment,
      chartedSurfaces: [],
      notes: [],
      serviceCodeSmartGroups: [],
      serviceCodeGroups: [],
      serviceCodes: [],
      invoices: [],
      basePrice: 0,
      ...overrides,
      feeSchedule: toNamedDocument(overrides.feeSchedule),
    };
  }

  static override allChartedSurfaces(
    treatment: IChartedTreatment
  ): IChartedSurface[] {
    return [
      ...treatment.chartedSurfaces,
      ...treatment.serviceCodes
        .map((code) => code.chartedSurfaces)
        .reduce((allSurfaces: IChartedSurface[], surfaces) => {
          return [...allSurfaces, ...surfaces];
        }, []),
    ];
  }

  static includesInvoice(
    treatment: IChartedTreatment,
    ref: DocumentReference
  ): boolean {
    return treatment.invoices
      .map((invoice) => invoice.invoiceRef)
      .includes(ref);
  }

  static async loadScopedTreatmentConfigs(
    treatments: IChartedTreatment[]
  ): Promise<WithRef<ITreatmentConfiguration>[]> {
    const configRefs = uniqBy(
      treatments.map((treatment) => treatment.config.ref),
      (ref) => ref.path
    );
    const configs = await asyncForEach(configRefs, (ref) =>
      Firestore.getDoc(ref)
    );
    return configs.filter((config) =>
      TreatmentConfiguration.hasScopedGroups(config)
    );
  }

  static changeFeeSchedule(
    treatment: IChartedTreatment,
    stepTreatments: IChartedTreatment[],
    manager: FeeScheduleManager,
    scopedConfigs: WithRef<ITreatmentConfiguration>[]
  ): IChartedTreatment {
    const treatmentConfig = scopedConfigs.find((config) =>
      isSameRef(config, treatment.config)
    );
    const convertedTreatment = ScopedGroupTranslator.convertScopedGroupCodes(
      treatment,
      manager.currentSchedule,
      treatmentConfig
    );
    return ChartedTreatment.applyFeeSchedule(
      convertedTreatment,
      stepTreatments,
      manager,
      true,
      true
    );
  }

  static applyFeeSchedule(
    treatment: IChartedTreatment,
    stepTreatments: IChartedTreatment[],
    manager: FeeScheduleManager,
    convertServiceCodes: boolean,
    changingFeeSchedule = false
  ): IChartedTreatment {
    const feeSchedule = manager.currentSchedule;
    treatment.feeSchedule = toNamedDocument(feeSchedule);
    const treatmentServiceCodes =
      PricedServiceCodeEntry.expandComplexPricedCodes(treatment.serviceCodes);

    const stepServiceCodes = flatten(
      stepTreatments.map((stepTreatment) =>
        stepTreatment.uuid === treatment.uuid
          ? treatmentServiceCodes
          : stepTreatment.serviceCodes
      )
    );

    const serviceCodes = treatmentServiceCodes.map((serviceCode) =>
      PricedServiceCodeEntry.applyFeeSchedule(
        serviceCode,
        stepServiceCodes,
        manager,
        convertServiceCodes,
        changingFeeSchedule
      )
    );

    const stepGroupCodes = flatten(
      stepTreatments
        .map((stepTreatment) =>
          stepTreatment.serviceCodeGroups.map(
            (serviceCodeGroup) => serviceCodeGroup.serviceCodes
          )
        )
        .reduce(reduceToSingleArrayFn, [])
    );

    const serviceCodeGroups = treatment.serviceCodeGroups.map((adaGroup) =>
      PricedServiceCodeGroup.applyFeeSchedule(
        adaGroup,
        stepGroupCodes,
        manager,
        convertServiceCodes,
        changingFeeSchedule
      )
    );

    const stepTreatmentSmartCodes = flatten(
      stepTreatments.map((stepTreatment) =>
        flatten(
          stepTreatment.serviceCodeSmartGroups.map(
            (smartGroup) => smartGroup.serviceCodes
          )
        )
      )
    );
    const stepSmartGroupServiceCodes =
      PricedServiceCodeEntry.expandComplexPricedCodes(stepTreatmentSmartCodes);

    const serviceCodeSmartGroups = treatment.serviceCodeSmartGroups.map(
      (smartGroup) =>
        ChartedServiceSmartGroup.applyFeeSchedule(
          smartGroup,
          stepSmartGroupServiceCodes,
          manager,
          convertServiceCodes,
          changingFeeSchedule
        )
    );

    const updatedTreatment = {
      ...treatment,
      serviceCodes,
      serviceCodeGroups,
      serviceCodeSmartGroups,
    };

    updatedTreatment.taxStatus = FeeSchedule.getTreatmentTaxStrategy(
      feeSchedule,
      treatment.config.ref
    );

    if (treatment.basePriceOverride !== 0 || changingFeeSchedule) {
      const scheduleRule = ChartedTreatment._resolveScheduleRule(
        feeSchedule,
        manager,
        treatment.config.ref
      );
      const existingRule = treatment.basePricePricingRule;
      const resolvedRule =
        scheduleRule && existingRule
          ? determinePricingRule(scheduleRule, existingRule)
          : scheduleRule;

      updatedTreatment.basePricePricingRule = resolvedRule;

      updatedTreatment.basePrice = ChartedTreatment.calculateBasePrice(
        updatedTreatment,
        feeSchedule,
        manager,
        resolvedRule
      );
    }

    return updatedTreatment;
  }

  static applyPackagePricing(
    treatment: IChartedTreatment,
    treatmentPackage: ITreatmentPackage
  ): IChartedTreatment {
    const serviceCodes = treatment.serviceCodes.map((serviceCode, index) =>
      ChartedTreatment.patchPackagePrice(
        treatmentPackage.priceOverrides,
        serviceCode,
        index,
        treatment.serviceCodes
      )
    );

    const serviceCodeGroups = treatment.serviceCodeGroups.map((group) => ({
      ...group,
      serviceCodes: group.serviceCodes.map((serviceCode, index) =>
        ChartedTreatment.patchPackagePrice(
          treatmentPackage.priceOverrides,
          serviceCode,
          index,
          group.serviceCodes
        )
      ),
    }));

    const serviceCodeSmartGroups = treatment.serviceCodeSmartGroups.map(
      (group) => ({
        ...group,
        serviceCodes: group.serviceCodes.map((serviceCode, index) =>
          ChartedTreatment.patchPackagePrice(
            treatmentPackage.priceOverrides,
            serviceCode,
            index,
            group.serviceCodes
          )
        ),
      })
    );

    return {
      ...treatment,
      basePriceOverride: treatmentPackage.basePriceOverride,
      serviceCodes,
      serviceCodeGroups,
      serviceCodeSmartGroups,
      treatmentPackageId: treatmentPackage.uid,
    };
  }

  static clearPackagePricing(treatment: IChartedTreatment): IChartedTreatment {
    const serviceCodes = treatment.serviceCodes.map((serviceCode) => ({
      ...serviceCode,
      priceOverride: undefined,
    }));
    const updated = {
      ...treatment,
      serviceCodes,
      basePriceOverride: undefined,
    };
    return {
      ...updated,
      treatmentPackageId: undefined,
    };
  }

  static patchPackagePrice(
    priceOverrides: ITreatmentPackagePriceOverride[],
    serviceCode: IPricedServiceCodeEntry,
    index: number,
    serviceCodes: IPricedServiceCodeEntry[]
  ): IPricedServiceCodeEntry {
    const packageCode = TreatmentPackage.getMappedPriceOverride(
      serviceCode,
      index,
      serviceCodes,
      priceOverrides
    );

    return {
      ...serviceCode,
      priceOverride: packageCode.price,
    };
  }

  static allADACodes(treatment: IChartedTreatment): IPricedServiceCodeEntry[] {
    return [
      ...treatment.serviceCodes,
      ...compact(
        treatment.serviceCodeSmartGroups.map((smartGroup) =>
          ChartedServiceSmartGroup.getSelected(smartGroup)
        )
      ),
      ...compact(
        treatment.serviceCodeGroups
          .filter((group) => group.type === ServiceCodeGroupType.Exclusive)
          .map((group) => ChartedServiceExclusiveGroup.getSelected(group))
      ),
    ];
  }

  static getInvoice(
    treatment: IChartedTreatment,
    ref: DocumentReference
  ): IInvoiceReference | undefined {
    return treatment.invoices.find(
      (invoice) => invoice.invoiceRef.path === ref.path
    );
  }

  static fromConfig(
    config: WithRef<ITreatmentConfiguration>,
    feeSchedule: WithRef<IFeeSchedule>,
    scopeRef: IScopeRef,
    attributedTo?: INamedDocument<IStaffer>
  ): IChartedTreatment {
    const exclusiveServiceCodeGroups = TreatmentConfiguration.getServiceGroups(
      config,
      [ServiceCodeGroupType.Exclusive],
      feeSchedule
    );
    const requiredServiceCodes = TreatmentConfiguration.getCombinedServiceCodes(
      config,
      [ServiceCodeGroupType.Required],
      feeSchedule
    );
    const serviceCodeSmartGroups =
      TreatmentConfiguration.getSmartGroups(config);

    const treatmentFee = FeeSchedule.getTreatmentFee(feeSchedule, config.ref);

    return ChartedTreatment.init({
      config: toNamedDocument(config),
      serviceCodeGroups: exclusiveServiceCodeGroups,
      serviceCodeSmartGroups,
      serviceCodes: requiredServiceCodes,
      basePrice: treatmentFee?.price ?? 0,
      basePricePricingRule: treatmentFee?.pricingRule,
      taxStatus: FeeSchedule.getTreatmentTaxStrategy(feeSchedule, config.ref),
      feeSchedule: toNamedDocument(feeSchedule),
      attributedTo,
      scopeRef,
    });
  }

  static applyPricing(
    treatment: IChartedTreatment,
    stepTreatments: IChartedTreatment[],
    feeScheduleManager: FeeScheduleManager,
    changingFeeSchedule = false,
    treatmentPackage?: ITreatmentPackage
  ): IChartedTreatment {
    const updatedTreatment = ChartedTreatment.applyFeeSchedule(
      treatment,
      stepTreatments,
      feeScheduleManager,
      changingFeeSchedule
    );
    return treatmentPackage
      ? ChartedTreatment.applyPackagePricing(updatedTreatment, treatmentPackage)
      : updatedTreatment;
  }

  static getBasePrice(treatment: IChartedTreatment): number {
    return treatment.basePriceOverride ?? treatment.basePrice ?? 0;
  }

  static calculateBasePrice(
    treatment: IChartedTreatment,
    feeSchedule: IFeeSchedule,
    manager: FeeScheduleManager,
    resolvedRule: IPricingRule | undefined
  ): number {
    if (feeSchedule.treatmentFeeMode === TreatmentFeeMode.FixedTotal) {
      const serviceCodesTotal = new ChartedItemTotalCalculator().serviceCodes(
        treatment
      );

      if (resolvedRule) {
        const rulePrice = FeeSchedule.getTreatmentFeeDefaultPrice(resolvedRule);
        if (rulePrice !== undefined) {
          return roundTo2Decimals(Math.max(0, rulePrice - serviceCodesTotal));
        }
      }

      const fixedTotal = FeeSchedule.getTreatmentFeePrice(
        feeSchedule,
        treatment.config.ref
      );
      return roundTo2Decimals(Math.max(0, fixedTotal - serviceCodesTotal));
    }

    if (resolvedRule) {
      const rulePrice = FeeSchedule.getTreatmentFeeDefaultPrice(resolvedRule);
      if (rulePrice !== undefined) {
        return rulePrice;
      }
    }

    return manager.getFeeByTreatment(treatment.config.ref);
  }

  private static _resolveScheduleRule(
    feeSchedule: IFeeSchedule,
    manager: FeeScheduleManager,
    treatmentRef: DocumentReference<ITreatmentConfiguration>
  ): IPricingRule | undefined {
    const directRule = FeeSchedule.getTreatmentFee(feeSchedule, treatmentRef)
      ?.pricingRule;
    if (directRule) {
      return directRule;
    }

    return manager.getDefaultTreatmentPricingRule(treatmentRef);
  }
}
